// ============================================================
// HOUSE OF CARDS — PsychosisEngine v1
// ------------------------------------------------------------
// The truth the player never learns: this is the Tenant timer.
// Wearing a different mask. It counts the same wall-clock the
// Tenant always counted — but to Tome it reads as his own mind
// slipping. The meter RISES toward a break instead of counting
// down toward expiry; mechanically identical, emotionally inverted.
//
// The Wendigo and the Raven can lean on the clock — speeding it
// (dread) or easing it (a moment of false calm). To the player
// that pressure is indistinguishable from psychosis. That's the
// point. `rate` is their hand on the dial:
//     rate > 1  → time bleeds faster  (the Wendigo presses in)
//     rate < 1  → time bleeds slower  (the Raven grants reprieve)
//     rate = 1  → ordinary unraveling
//
// EntityTimer.js stays SACRED and byte-identical. This file does
// not import or modify it — it mirrors its exact wall-clock and
// pause discipline so the two can never drift.
//
// A "collectible secured" event (resetSignal) clears the meter:
// finding an object reassures Tome it was real, that he placed it
// himself, that he isn't mad. The relief never lasts.
// ============================================================
import React, { useEffect, useRef, useState } from "react";
import { RM } from "./theme";

// Base window before a break, matched to the Tenant's 6 min cadence.
export const PSYCHOSIS_WINDOW_MS = 6 * 60 * 1000;

// FIVE distinct tiers so the meter rises in lockstep with the five
// heartbeat tracks (heartbeat_tier0..4). The ramp runs green → glowing
// purple. Each tier escalates jumpscare frequency AND the chance a scare
// is the lethal kind — in EVERY mode (Cursed just climbs faster via
// `rate`). Thresholds are % of the window filled.
//   0 LUCID     — quiet. scares rare, almost never lethal.        (green)
//   1 FRAYING   — first cracks. scares step up.                   (chartreuse)
//   2 SLIPPING  — losing the thread. scares frequent.             (ember)
//   3 SPLITTING — the seams go. deadly scares likely.             (magenta)
//   4 BREAKING  — the edge of the break. nearly all deadly.       (purple)
export const PSY_TIERS = [
  {
    id: "lucid",
    name: "LUCID",
    blurb: "your mind holds",
    max: 20,                 // 0–20%
    scareMult: 1.0,          // scheduler delay multiplier (1 = baseline)
    lethalChance: 0.08,      // P(a fired scare is the deadly kind)
    color: "psyRamp0",
  },
  {
    id: "fraying",
    name: "FRAYING",
    blurb: "something's loose in here",
    max: 40,                 // 20–40%
    scareMult: 0.74,
    lethalChance: 0.22,
    color: "psyRamp1",
  },
  {
    id: "slipping",
    name: "SLIPPING",
    blurb: "it's getting loud in here",
    max: 60,                 // 40–60%
    scareMult: 0.56,
    lethalChance: 0.40,
    color: "psyRamp2",
  },
  {
    id: "splitting",
    name: "SPLITTING",
    blurb: "you can't tell what's real",
    max: 80,                 // 60–80%
    scareMult: 0.42,
    lethalChance: 0.58,
    color: "psyRamp3",
  },
  {
    id: "breaking",
    name: "BREAKING",
    blurb: "the house is inside your head",
    max: 100,                // 80–100%
    scareMult: 0.30,         // scares come fast
    lethalChance: 0.78,
    color: "psyRamp4",
  },
];

export function tierForPct(pct) {
  for (let i = 0; i < PSY_TIERS.length; i++) {
    if (pct <= PSY_TIERS[i].max) return i;
  }
  return PSY_TIERS.length - 1;
}

export default function PsychosisEngine({
  resetSignal,   // change this value to reset the meter (collectible secured)
  reduceSignal,  // {n, frac} — bump n to subtract `frac` of current dread (mushroom, cursed)
  paused,        // freeze the wall-clock (jumpscare, inspect, menu)
  rate = 1,      // Wendigo/Raven hand on the dial; 1 = normal
  onBreak,       // () => void — meter hit 100%, Tome's mind gives
  onTier,        // (tierIndex) => void — fires when the tier changes
  onPct,         // (pct:number) => void — raw % each tick (heartbeat audio)
  hidden,        // optional: hide the HUD (e.g., Day 1 tutorial calm)
}) {
  // We track ELAPSED toward the window rather than remaining, so the
  // bar fills as dread mounts. Same wall-clock math as the Tenant.
  const [elapsed, setElapsed] = useState(0);
  const accumRef = useRef(0);          // ms of dread banked so far
  const lastTickRef = useRef(Date.now());
  const firedRef = useRef(false);
  const rateRef = useRef(rate);
  rateRef.current = rate;              // always read the live rate in the tick
  const tierRef = useRef(0);           // last reported tier index
  const onTierRef = useRef(onTier);
  onTierRef.current = onTier;          // live callback without re-arming the tick
  const onPctRef = useRef(onPct);
  onPctRef.current = onPct;            // live raw-% callback (heartbeat audio)

  // Collectible secured → the meter empties. Tome breathes again.
  useEffect(() => {
    accumRef.current = 0;
    lastTickRef.current = Date.now();
    firedRef.current = false;
    setElapsed(0);
    onPctRef.current && onPctRef.current(0);
    if (tierRef.current !== 0) {
      tierRef.current = 0;
      onTierRef.current && onTierRef.current(0);
    }
  }, [resetSignal]);

  // Partial relief (the mushroom in Cursed Mode clears HALF). Unlike a
  // full reset, this scales the banked dread down by (1 - frac) and
  // re-anchors the clock so no paused time leaks in. The tier/pct
  // callbacks fire so the HUD and heartbeat follow the drop instantly.
  useEffect(() => {
    if (!reduceSignal || !reduceSignal.n) return;
    const frac = Math.max(0, Math.min(1, reduceSignal.frac ?? 0.5));
    accumRef.current = accumRef.current * (1 - frac);
    lastTickRef.current = Date.now();
    firedRef.current = false;
    const e = Math.min(accumRef.current, PSYCHOSIS_WINDOW_MS);
    setElapsed(e);
    const newPct = (e / PSYCHOSIS_WINDOW_MS) * 100;
    onPctRef.current && onPctRef.current(newPct);
    const nt = tierForPct(newPct);
    if (nt !== tierRef.current) {
      tierRef.current = nt;
      onTierRef.current && onTierRef.current(nt);
    }
  }, [reduceSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pause must freeze the WALL-CLOCK, not just the display — same
  // discipline as the Tenant timer. We re-anchor lastTick on resume
  // so paused time is never banked as dread.
  useEffect(() => {
    if (!paused) lastTickRef.current = Date.now();
  }, [paused]);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => {
      const now = Date.now();
      const dt = now - lastTickRef.current;
      lastTickRef.current = now;
      // The hand on the dial scales how much real time becomes dread.
      accumRef.current += dt * (rateRef.current > 0 ? rateRef.current : 0);
      const e = Math.min(accumRef.current, PSYCHOSIS_WINDOW_MS);
      setElapsed(e);
      // Raw % for the audio heartbeat (its own finer 5-tier mapping).
      onPctRef.current && onPctRef.current((e / PSYCHOSIS_WINDOW_MS) * 100);
      // Tier change → tell the scheduler the rules just shifted.
      const nowTier = tierForPct((e / PSYCHOSIS_WINDOW_MS) * 100);
      if (nowTier !== tierRef.current) {
        tierRef.current = nowTier;
        onTierRef.current && onTierRef.current(nowTier);
      }
      if (accumRef.current >= PSYCHOSIS_WINDOW_MS && !firedRef.current) {
        firedRef.current = true;
        onBreak && onBreak();
      }
    }, 250);
    return () => clearInterval(t);
  }, [paused, onBreak]);

  if (hidden) return null;

  const pct = Math.min(100, (elapsed / PSYCHOSIS_WINDOW_MS) * 100);
  const ti = tierForPct(pct);
  const tier = PSY_TIERS[ti];
  const tierColor = RM[tier.color] || RM.vein;
  const danger = ti >= PSY_TIERS.length - 1;

  // Three distinct segments so the level is legible at a glance.
  // Each segment fills only within its own band of the meter.
  const segFill = (i) => {
    const lo = i === 0 ? 0 : PSY_TIERS[i - 1].max;
    const hi = PSY_TIERS[i].max;
    if (pct <= lo) return 0;
    if (pct >= hi) return 100;
    return ((pct - lo) / (hi - lo)) * 100;
  };

  return (
    <div style={S.wrap} className={danger ? "vein-pulse" : ""}>
      <div style={S.head}>
        <span style={{ ...S.tierName, color: tierColor }}>{tier.name}</span>
        <span style={S.blurb}>{tier.blurb}</span>
      </div>
      <div style={S.segRow}>
        {PSY_TIERS.map((t, i) => {
          const c = RM[t.color] || RM.vein;
          const reached = ti >= i;
          // The active (top reached) segment glows; the bloom is strongest
          // on the final purple tier so BREAKING visibly pulses with light.
          const isActive = reached && i === ti;
          const glow =
            isActive && i === PSY_TIERS.length - 1
              ? `0 0 8px ${c}, 0 0 16px ${c}aa`     // glowing purple
              : isActive
              ? `0 0 6px ${c}cc`
              : "none";
          return (
            <div key={t.id} style={S.segOuter} title={t.name}>
              <div
                style={{
                  ...S.segInner,
                  width: `${segFill(i)}%`,
                  background: c,
                  opacity: reached ? 1 : 0.9,
                  boxShadow: glow,
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

const S = {
  wrap: {
    position: "fixed",
    top: 64,
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    padding: "6px 14px",
    background: "rgba(7,5,4,0.85)",
    border: `1px solid ${RM.vein}`,
    borderRadius: 8,
    zIndex: 50,
  },
  head: {
    display: "flex",
    alignItems: "baseline",
    gap: 8,
  },
  tierName: {
    fontFamily: RM.fontD,
    fontSize: 12,
    letterSpacing: 3,
  },
  blurb: {
    fontFamily: RM.font,
    fontStyle: "italic",
    fontSize: 11,
    color: RM.boneFaint,
  },
  segRow: {
    display: "flex",
    gap: 3,
  },
  segOuter: {
    width: 34,
    height: 6,
    background: RM.char1,
    borderRadius: 4,
    overflow: "hidden",
  },
  segInner: { height: "100%", transition: "width 250ms linear" },
};
